<template>
  <div :class="cn('j-filter-bar w-full rounded-sm border bg-card text-card-foreground shadow-sm', props.class)">
    <!-- Row 1: toolbar -->
    <div class="flex items-center justify-between px-2 py-1">
      <div class="flex items-center gap-1">
        <button
          v-if="collapsible"
          type="button"
          class="flex items-center justify-center h-6 w-6 rounded hover:bg-accent hover:text-accent-foreground transition-colors"
          @click="toggleCollapsed"
        >
          <ChevronDown
            :class="[
              'h-3.5 w-3.5 transition-transform',
              isExpanded ? 'rotate-0' : '-rotate-90',
            ]"
          />
        </button>
        <!-- 타이틀 -->
        <JLabel
          v-if="title"
          :text="title"
          class="text-sm font-semibold text-foreground"
        />
        <!-- 선택된 필터 뱃지 표시 -->
        <div v-if="activeFilters.length > 0" class="flex items-center gap-1 flex-wrap">
          <JBadge
            v-for="filter in activeFilters"
            :key="filter.key"
            variant="secondary"
            size="sm"
            class="flex items-center gap-1 cursor-default"
          >
            <span class="text-muted-foreground">{{ filter.label }}:</span>
            <span>{{ filter.value }}</span>
            <button
              type="button"
              class="ml-0.5 rounded-full hover:bg-gray-300 p-0.5 transition-colors"
              @click.stop="removeFilter(filter.key)"
            >
              <X class="h-3 w-3" />
            </button>
          </JBadge>
        </div>
      </div>
      <div class="flex items-center gap-1">
        <slot name="actions" />
        <JButton
          v-if="showResetButton"
          variant="secondary"
          size="sm"
          @click="handleReset"
        >
          {{ resetButtonText }}
        </JButton>
        <JButton
          v-if="showSearchButton"
          styletype="primary"
          size="sm"
          @click="handleSearch"
        >
          {{ searchButtonText }}
        </JButton>
      </div>
    </div>

    <!-- Row 2: filters (반응형 그리드: max 4열, 자동 축소) -->
    <div v-show="isExpanded" class="px-2 pb-2">
      <div class="filter-fields-grid">
        <slot name="filters" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ChevronDown, X } from 'lucide-vue-next'
import JBadge from '@/components/atoms/JBadge.vue'
import JButton from '@/components/atoms/JButton.vue'
import JLabel from '@/components/atoms/JLabel.vue'
import { cn } from '@/lib/utils'

/** 활성 필터 아이템 타입 */
export interface ActiveFilterItem {
  /** 필터 식별 키 */
  key: string
  /** 표시할 라벨 (필터명) */
  label: string
  /** 표시할 값 */
  value: string
}

/** 필터 설정 타입 */
export interface FilterDisplayItem {
  /** 표시할 라벨 */
  label: string
  /** 값을 표시용 문자열로 변환 (예: combo value -> label) */
  displayValue?: (value: unknown) => string
}

export interface JFilterBarProps {
  /** 추가 클래스 (외부 커스터마이징용) */
  class?: string
  /** 필터바 타이틀 */
  title?: string
  /** 필터 접힘 상태 (v-model 지원) */
  collapsed?: boolean
  /** 접기/펼치기 가능 여부. false면 토글 버튼 숨김 & 필터 항상 표시 */
  collapsible?: boolean
  /** 필터 값 객체 (v-model:filterValues 지원) */
  filterValues?: Record<string, unknown>
  /** 필터 표시 설정 (label, displayValue 등) */
  filterDisplay?: Record<string, FilterDisplayItem>
  /** 초기화 버튼 표시 여부 */
  showResetButton?: boolean
  /** 조회 버튼 표시 여부 */
  showSearchButton?: boolean
  /** 초기화 버튼 텍스트 */
  resetButtonText?: string
  /** 조회 버튼 텍스트 */
  searchButtonText?: string
}

const props = withDefaults(defineProps<JFilterBarProps>(), {
  collapsed: true,
  collapsible: true,
  filterValues: () => ({}),
  filterDisplay: () => ({}),
  showResetButton: false,
  showSearchButton: false,
  resetButtonText: '초기화',
  searchButtonText: '조회',
})

const emit = defineEmits<{
  'update:collapsed': [value: boolean]
  'update:filterValues': [value: Record<string, unknown>]
  /** 조회 버튼 클릭 */
  search: []
  /** 초기화 버튼 클릭 */
  reset: []
}>()

const isExpanded = computed(() => {
  if (!props.collapsible) return true
  return !props.collapsed
})

/** 값이 비어있는지 확인 */
function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string' && value.trim() === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

/** filterValues + filterDisplay 기반으로 활성 필터 목록 자동 생성 */
const activeFilters = computed<ActiveFilterItem[]>(() => {
  const filters: ActiveFilterItem[] = []

  for (const [key, config] of Object.entries(props.filterDisplay)) {
    const value = props.filterValues[key]
    if (isEmpty(value)) continue

    const displayValue = config.displayValue ? config.displayValue(value) : String(value)
    if (displayValue.trim() === '') continue

    filters.push({
      key,
      label: config.label,
      value: displayValue,
    })
  }

  return filters
})

function toggleCollapsed() {
  emit('update:collapsed', !props.collapsed)
}

function handleReset() {
  // filterValues의 모든 값을 초기화
  const resetValues: Record<string, unknown> = {}
  for (const key of Object.keys(props.filterValues)) {
    const currentValue = props.filterValues[key]
    if (typeof currentValue === 'string') {
      resetValues[key] = ''
    } else if (Array.isArray(currentValue)) {
      resetValues[key] = []
    } else {
      resetValues[key] = null
    }
  }
  emit('update:filterValues', resetValues)
  emit('reset')
}

function handleSearch() {
  emit('search')
}

function removeFilter(key: string) {
  // filterValues 업데이트 (해당 키 값을 초기화)
  const newValues = { ...props.filterValues }
  const currentValue = newValues[key]

  // 타입에 따라 적절한 초기값으로 설정
  if (typeof currentValue === 'string') {
    newValues[key] = ''
  } else if (Array.isArray(currentValue)) {
    newValues[key] = []
  } else {
    newValues[key] = null
  }

  emit('update:filterValues', newValues)
}
</script>

<style scoped>
/* 활성 필터 배지 — cursor-default(JBadge)의 기본 상태 구분감 보정 */
:deep(.cursor-default) {
  background-color: hsl(var(--muted) / 0.82) !important;
  border: 1px solid hsl(var(--border) / 0.9) !important;
}
:deep(.cursor-default) > button {
  background-color: hsl(var(--background) / 0.98);
  color: hsl(var(--foreground) / 0.9);
}
:deep(.cursor-default) > button:hover {
  background-color: hsl(var(--muted) / 1);
  color: hsl(var(--foreground));
}

/* 필터 필드 반응형 그리드: max 4열, 자동 축소 (4 → 3 → 2 → 1) */
.filter-fields-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(max(25% - 0.75rem, 220px), 1fr));
  gap: 0.375rem 0.75rem;
  --label-w: 5rem; /* 필터 컨텍스트: 라벨 컴팩트 (80px) → 필드에 공간 확보 */
}

/* ========================================
   패턴 3: Tabs 아래 배치 시 연결 스타일
   ======================================== */

:deep([data-state="active"]) > .j-filter-bar {
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

:deep([role="tabpanel"]) .j-filter-bar {
  border-top: none;
}
</style>
